/**
 * @file draftPricing.js
 * @version 1.1.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-21
 * @description Draft invoices awaiting pricing (Cam, 2026-10-08) on the
 *              client: what the screens say about them, and the approval rule
 *              the Price Orders panel mirrors (server: utils/draftPricingRules.js
 *              — keep the two in step).
 */

/** Sales accounts whose book-to-account till sales are priced afterwards. */
export const TILL_DRAFT_ACCOUNT_CODES = ['200C', '200M', '200N', '207', '207A'];

export const isTillDraftAccountCode = (code) => (
  code !== null && code !== undefined && TILL_DRAFT_ACCOUNT_CODES.includes(String(code).trim().toUpperCase())
);

/**
 * Does this line still need a price before the draft can be approved? A
 * delivery draft: every line. A till draft: only its pricing-account lines.
 */
export function lineNeedsPrice(price, line, source) {
  const n = parseFloat(price);
  if (Number.isFinite(n) && n > 0) return false;
  if (source === 'till_account') return isTillDraftAccountCode(line?.account_code);
  return true;
}

/** Where a draft came from, in words. */
export function draftSourceLabel(draft) {
  if (!draft) return '';
  if (draft.pricing_source === 'delivery') return draft.order_number ? `Delivered order ${draft.order_number}` : 'Delivered order';
  return 'Till sale, booked to account';
}

/** What Update Delivery did to the order's draft invoice, in words (null = nothing). */
export function draftInvoiceMessage(draft) {
  if (!draft) return null;
  if (draft.action === 'failed') {
    return `The delivery is saved, but its draft invoice could not be ${draft.error ? `made (${draft.error})` : 'made'} — it is tried again automatically, or set Delivered again.`;
  }
  if (!draft.invoice_no) return null;
  const xero = draft.xero_warning
    ? `; not in Xero yet (${draft.xero_warning}) — it is sent again automatically`
    : '';
  const moved = draft.redated?.message ? ` ${draft.redated.message}` : '';
  if (draft.action === 'create') {
    return `Draft invoice ${draft.invoice_no} raised for the supplied quantities${xero || ' and sent to Xero as a draft'} — price and approve it in Pricing & Costing.${moved}`;
  }
  if (draft.action === 'refresh') {
    return `Draft invoice ${draft.invoice_no} updated to the supplied quantities${xero} — still waiting in Pricing & Costing.`;
  }
  if (draft.action === 'delete') {
    return `Draft invoice ${draft.invoice_no} deleted — the order is no longer delivered.`
      + (draft.xero_warning ? ` ${draft.xero_warning}` : '');
  }
  return null;
}
